import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

const KEY = "financial-forecast";

// ─── Types ───────────────────────────────────────────────────────────

export type EventKind =
  | "payroll"
  | "headcount"
  | "vendor"
  | "new_revenue"
  | "churn"
  | "one_off";

/**
 * Headcount planning progression. It moves week to week — that's the point,
 * it's a planning state, not an HR record. Only `baked_in` changes the
 * arithmetic: it means the pay calendar already includes this person, so
 * adding their cost again double-counts them. The other three all mean "not in
 * the payroll numbers yet" and differ only in confidence.
 */
export type HeadcountState =
  | "speculative"
  | "offer_accepted"
  | "started"
  | "baked_in";

/** Churn confidence — deliberately mirrors the headcount progression, so
 *  "exclude speculative" is one filter rather than a per-type rule. */
export type ChurnState = "speculative" | "notice_given" | "confirmed";

export type Certainty = "speculative" | "expected" | "committed";

export interface CalendarEvent {
  id: string;
  kind: EventKind;
  title: string;
  subtitle: string | null;
  /** When the thing happens: a hire's start, the last day we bill. */
  effective_date: string | null;
  /** When money actually moves. What the cash engine consumes. */
  cash_date: string;
  /** Signed: negative leaves the bank. */
  amount: number;
  state: string | null;
  editable: boolean;
  /** True when cash lands on a different day than the event itself. */
  is_deferred: boolean;
  meta: Record<string, unknown>;
}

export interface MonthTotals {
  event_count: number;
  /** Events whose CASH lands inside this month — the ones that count. */
  settling_count: number;
  inflow: number;
  outflow: number;
  net: number;
  by_kind: Record<string, number>;
  /** Shown this month, but the money moves in another one. */
  deferred_count: number;
}

export interface MonthPayload {
  year: number;
  month: number;
  start: string;
  end: string;
  events: CalendarEvent[];
  totals: MonthTotals;
}

export interface Headcount {
  id: string;
  name: string;
  /** The ACCRUAL date — cash lands in the next pay run. */
  start_date: string;
  /** Negative for a hire (cost added), positive for a departure. */
  add_per_paycheck: number;
  plan_state: HeadcountState;
  role: string | null;
  department: string | null;
  annual_salary: number | null;
  note: string | null;
  is_active: boolean;
}

export interface Churn {
  id: string;
  qb_id: number | null;
  account_name: string;
  state: ChurnState;
  notice_date: string | null;
  /** Last day we bill. Cash stops one collection lag after this. */
  end_date: string;
  mrr: number;
  terms_days: number;
  reason: string | null;
  source: string;
  /** True once the monthly close (reconciliation R-04) settled the dates. */
  confirmed_by_recon: boolean;
  is_active: boolean;
}

export interface OneOff {
  id: string;
  label: string;
  category: string | null;
  /** Without a bucket it shows on the calendar but never reaches the forecast. */
  bucket_name: string | null;
  effective_date: string | null;
  cash_date: string;
  amount: number;
  certainty: Certainty;
  notes: string | null;
  is_active: boolean;
}

export interface Vendor {
  id: string;
  vendor: string;
  due_date: string;
  cash_week: string;
  forecast_amount: number;
  notes: string | null;
}

export interface Revenue {
  id: string;
  contract: string;
  annual_amount: number;
  start_date: string;
  terms_days: number;
  buffer_days: number;
  first_cash_week: string;
  notes: string | null;
  is_active: boolean;
}

export interface AssumptionsPayload {
  headcount: Headcount[];
  churn: Churn[];
  one_offs: OneOff[];
  vendors: Vendor[];
  revenue: Revenue[];
}

export interface ChurnSyncResult {
  added: number;
  updated: number;
  deactivated: number;
  settled_total: number;
}

// ─── Queries ─────────────────────────────────────────────────────────

export function useForecastMonth(year: number, month: number) {
  return useQuery({
    queryKey: [KEY, "calendar", year, month],
    queryFn: () =>
      apiFetch<MonthPayload>(`/financial_forecast/calendar/${year}/${month}`),
    staleTime: 30_000,
  });
}

export function useAssumptions() {
  return useQuery({
    queryKey: [KEY, "assumptions"],
    queryFn: () => apiFetch<AssumptionsPayload>("/financial_forecast/assumptions"),
    staleTime: 30_000,
  });
}

/** Invalidate everything the calendar draws from. */
function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: [KEY] });
    // Assumptions drive the cash forecast too.
    void qc.invalidateQueries({ queryKey: ["cash-flow"] });
  };
}

// ─── Mutations ───────────────────────────────────────────────────────

type Body = Record<string, unknown>;

function useCreate<T>(path: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: Body) =>
      apiFetch<T>(`/financial_forecast/${path}`, { method: "POST", body }),
    onSuccess: invalidate,
  });
}

function useUpdate<T>(path: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (vars: { id: string; body: Body }) =>
      apiFetch<T>(`/financial_forecast/${path}/${vars.id}`, {
        method: "PATCH",
        body: vars.body,
      }),
    onSuccess: invalidate,
  });
}

function useRemove(path: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/financial_forecast/${path}/${id}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });
}

export const useCreateHeadcount = () => useCreate<Headcount>("headcount");
export const useUpdateHeadcount = () => useUpdate<Headcount>("headcount");
export const useDeleteHeadcount = () => useRemove("headcount");

export const useCreateChurn = () => useCreate<Churn>("churn");
export const useUpdateChurn = () => useUpdate<Churn>("churn");
export const useDeleteChurn = () => useRemove("churn");

export const useCreateOneOff = () => useCreate<OneOff>("one-offs");
export const useUpdateOneOff = () => useUpdate<OneOff>("one-offs");
export const useDeleteOneOff = () => useRemove("one-offs");

export const useCreateVendor = () => useCreate<Vendor>("vendors");
export const useUpdateVendor = () => useUpdate<Vendor>("vendors");
export const useDeleteVendor = () => useRemove("vendors");

export const useCreateRevenue = () => useCreate<Revenue>("revenue");
export const useUpdateRevenue = () => useUpdate<Revenue>("revenue");
export const useDeleteRevenue = () => useRemove("revenue");

/**
 * Pull the monthly close's settled churn (reconciliation R-04) into the
 * forecast. The close owns the truth about churn dates; the forecast owns the
 * churn nobody has confirmed yet.
 */
export function useSyncChurnFromRecon() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () =>
      apiFetch<ChurnSyncResult>("/financial_forecast/churn/sync", {
        method: "POST",
      }),
    onSuccess: invalidate,
  });
}
